import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Close button on the backdrop beside a side panel (the Navbar drawer and
 * the Popover side sheet). It is a child of the panel, so it stays in the
 * dialog's focus order and moves with the panel. `side` is the edge the
 * panel is docked to; the button sits past the panel's other edge.
 * It is a white icon with no fill of its own, so it relies on the panel's
 * dimmed backdrop to read: it is only for panels that always dim the page.
 * The 32px button centers on the panel's header row:
 * - `compact`: the Navbar compact row, its `xs` padding plus half of a 20px
 *   icon with `xxs` padding;
 * - `title`: the Navbar title row, its `lg` padding plus half the h4 line
 *   (`0.5lh` under `text-h4`);
 * - `dialogTitle`: the dialog title row, its `md` padding plus half the h4
 *   line.
 */
export const outsideCloseVariants = cva(
  'absolute flex size-8 items-center justify-center rounded-xs text-white outline-none focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-offset-focus focus-visible:ring-offset-1 [&_svg]:size-6',
  {
    variants: {
      side: {
        left: 'left-full ml-md',
        right: 'right-full mr-md',
      },
      header: {
        compact:
          'top-[calc(theme(spacing.xs)+theme(spacing.xxs)+10px-theme(spacing.4))]',
        title: 'text-h4 top-[calc(theme(spacing.lg)+0.5lh-theme(spacing.4))]',
        dialogTitle:
          'text-h4 top-[calc(theme(spacing.md)+0.5lh-theme(spacing.4))]',
      },
    },
    defaultVariants: {
      side: 'left',
      header: 'compact',
    },
  },
);

export type OutsideCloseVariantProps = VariantProps<
  typeof outsideCloseVariants
>;
